import {
  Box,
  BoxProps,
  CardHeaderProps,
  Paper,
  PaperProps,
  Theme,
  mergeSlotProps,
} from '@mui/material';
import {FC, PropsWithChildren, ReactNode, Suspense, useId, useRef} from 'react';
import {PanelFooterProps} from './PanelFooter.tsx';
import PanelToggleButton from './PanelToggleButton.tsx';
import PanelHeader, {
  PanelHeaderProps,
} from '@components/panels/PanelHeader.tsx';
import LoadingMessage from '@src/components/common/LoadingMessage.tsx';
import {DrawersValueType} from '@src/constants';
import {SIDE_PANEL_EXPAND_KEY} from '@src/constants/panels.js';
import dimensions from '@src/styles/dimensions.ts';

// eslint-disable-next-line react-refresh/only-export-components
export const POINTER_EVENTS_PASSING_SX = {
  // Enable pointer events passthrough on main `SidePanels` container, but capture by the rest
  // Child nodes i.e. `SidePanel`-s or additional wrappers should't stretch vertically completely if not necessary.
  pointerEvents: 'none',
  '> *': {pointerEvents: 'auto'},
} as const;

export type SidePanelProps = PropsWithChildren<{
  title: ReactNode;
  rootProps?: PaperProps;
  headerProps?: PanelHeaderProps;
  showHeader?: boolean;
  footerProps?: PanelFooterProps;
}>;

export const SidePanel: FC<SidePanelProps> = ({
  title,
  children,
  rootProps = {},
  showHeader = true,
  headerProps: {
    action: headerAction,
    icon,
    rootProps: headerRootProps,
    ...otherHeaderProps
  } = {},
}) => {
  const titleId = useId();
  const blockSize = `calc(100% - ${dimensions.statusbar.height + 2 * dimensions.map.toolMargin}px)`;

  return (
    <Paper
      component="aside"
      elevation={0}
      aria-labelledby={titleId}
      {...mergeSlotProps<PaperProps>(rootProps, {
        sx: {
          alignSelf: 'flex-start',
          boxShadow: theme => theme.shadows[1],
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          inlineSize: `${dimensions.leftSidebar.width}px`,
          blockSize,
          marginBlockStart: `${dimensions.map.toolMargin}px`,
          marginInlineStart: `${dimensions.map.toolMargin}px`,
        },
      })}
    >
      {showHeader && (
        <PanelHeader
          title={title}
          icon={icon}
          action={headerAction}
          rootProps={mergeSlotProps<CardHeaderProps>(
            {id: titleId},
            headerRootProps ?? {},
          )}
          {...otherHeaderProps}
        />
      )}
      <Suspense fallback={<LoadingMessage />}>{children}</Suspense>
    </Paper>
  );
};

export type SidePanelsProps = PropsWithChildren<{
  expand?: boolean;
  leftSideNavigationWidth?: number;
  width?: number;
  drawer?: DrawersValueType;
  transition?: (theme: Theme) => string;
  onTransitionEnd?: () => void;
  onExpand?: (expanded: boolean) => void;
}>;

export const SidePanels: FC<SidePanelsProps> = ({
  expand = true,
  leftSideNavigationWidth = 0,
  onExpand,
  transition,
  onTransitionEnd,
  children,
  ...props
}) => {
  const rootRef = useRef<HTMLElement>(null);
  const width =
    dimensions.leftSidebar.width +
    dimensions.map.toolMargin +
    leftSideNavigationWidth;

  const toggleExpand = () => {
    const newStateValue = !expand;
    localStorage.setItem(SIDE_PANEL_EXPAND_KEY, newStateValue.toString());
    onExpand?.(newStateValue);
  };

  return (
    <Box
      ref={rootRef}
      className="side-panels"
      {...mergeSlotProps<BoxProps>(
        {
          sx: theme => ({
            position: 'relative',
            zIndex: 'drawer',
            display: 'flex',
            blockSize: `100%`,
            marginInlineStart: `${expand ? 0 : -width}px`,
            transition: transition?.(theme),
            ...POINTER_EVENTS_PASSING_SX,
          }),
          onTransitionEnd: e => {
            if (e.target === rootRef.current) {
              onTransitionEnd?.();
            }
          },
        },
        props,
      )}
    >
      <PanelToggleButton
        expand={expand}
        onExpand={toggleExpand}
        rootProps={{
          sx: {
            insetInlineEnd: 0,
            insetBlockStart:
              dimensions.sidebarTab.insetBlock + dimensions.map.toolMargin,
            position: 'absolute',
            transform: 'translateX(100%)',
          },
        }}
        iconProps={{
          sx: theme => ({
            transition: transition?.(theme),
          }),
        }}
      />
      {children}
    </Box>
  );
};

export default SidePanels;
